import { useQuery } from "@tanstack/react-query";
import { orderBy } from "lodash";
import { Messages } from "../../../core/api";
import useApi from "../../../shared/hooks/useApi";
import { PatientId } from "../../../shared/schema/schema";
import { getFullName } from "../../../shared/utils/get-full-name";
import { isDefined } from "../../../utils";
import SendEmailButton from "../../../shared/components/SendEmailButton";
import { ButtonProps } from "@chakra-ui/react";

interface Props {
  patientId: PatientId;
  buttonProps?: ButtonProps;
  text?: string;
}

/** Prefer using IntakePatientSendEmailButton over this when you know it's an intake patient */
export default function PatientSendEmailButton(props: Props) {
  const { queries } = useApi();

  const emailsQuery = useQuery({
    ...queries.patient.get(props.patientId),
    select: (patient) => {
      const emails: Messages["CommCenterEmailAddressObject"][] = [];
      if (isDefined(patient.email) && patient.email !== "") {
        emails.push({
          name: getFullName(patient),
          email: patient.email,
        });
      }

      const contacts = orderBy(patient.contacts, (contact) => contact.id, "asc");
      contacts.forEach((contact) => {
        if (isDefined(contact.email) && patient.email !== "") {
          emails.push({
            name: getFullName(contact),
            email: contact.email,
          });
        }
      });

      return emails;
    },
  });

  if (emailsQuery.isError) {
    return (
      <SendEmailButton
        buttonProps={props.buttonProps}
        emails={[]}
        isDisabled={true}
        isLoading={false}
        newTicketDefaultValues={{
          topic: "Patient",
          caregiverId: null,
          patientId: props.patientId,
        }}
        text="Error while loading email button"
      />
    );
  }

  return (
    <SendEmailButton
      buttonProps={props.buttonProps}
      defaultAssignedTeam="Staffing"
      emails={emailsQuery.data ?? []}
      isLoading={emailsQuery.isLoading}
      newTicketDefaultValues={{
        topic: "Patient",
        caregiverId: null,
        patientId: props.patientId,
      }}
      text={props.text}
    />
  );
}
